import React, { useEffect } from 'react'
import {
  Card,
  Divider,
  Col,
  Form,
  Row,
  Skeleton,
  Space,
  theme,
  Typography,
  Button,
} from 'antd'
import { CheckCircleFilled } from '@ant-design/icons'
import WebhookForm from '../WebhookForm'
import GoogleCalendarForm from '../GoogleCalendarForm'
import ChatIntegrationForm from '../ChatIntegrationForm'
import CatalogIntegrationForm from '../CatalogIntegrationForm'
import KnowledgeForm from '../KnowledgeForm'
import NativeForm from "../NativeForm";
import CRMForm from "../CRMForm";
import MetaAdsForm from "../MetaAdsForm";
import FeegowForm from "../FeegowForm";

export default function ActionFormItems({
  form,
  trigger,
  parameters,
  initValues,
  catalogIntegrationData,
  loading,
  cards = [],
  isTemplate = false,
  metaAdsIntegrationId,
}) {
  return (
    <>
      <Typography.Text level={5}>
        A ação é disparada no momento em que o gatilho for acionado. Você pode
        usar os valores dos parametros encontrados em qualquer campo usando
        "$variavel".
      </Typography.Text>
      <Divider />
      <ActionItemList
        form={form}
        trigger={trigger}
        parameters={parameters}
        initValues={initValues}
        catalogIntegrationData={catalogIntegrationData}
        loading={loading}
        cards={cards}
        isTemplate={isTemplate}
        metaAdsIntegrationId={metaAdsIntegrationId}
      />
    </>
  )
}

function ActionItemList({
  form,
  trigger,
  parameters,
  initValues,
  catalogIntegrationData,
  loading,
  cards,
  isTemplate,
  metaAdsIntegrationId,
}) {
  return (
    <Form.List name="actions">
      {(fields, { add, remove }) => {
        if (fields.length === 0 && !isTemplate) add({})
        return (
          <>
            {fields.map((field) => (
              <ActionItem
                key={field.key}
                field={field}
                form={form}
                trigger={trigger}
                parameters={parameters}
                initValues={initValues !== -1 ? initValues : null}
                catalogIntegrationData={catalogIntegrationData}
                loading={loading}
                cards={cards}
                remove={remove}
                isTemplate={isTemplate}
                fieldsLength={fields.length}
                metaAdsIntegrationId={metaAdsIntegrationId}
              />
            ))}
            {isTemplate && fields.length < 3 && (
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block>
                  Adicionar Ação
                </Button>
              </Form.Item>
            )}
          </>
        )
      }}
    </Form.List>
  )
}

function ActionItem({
  field,
  form,
  trigger,
  parameters,
  initValues,
  catalogIntegrationData,
  loading,
  cards,
  remove,
  isTemplate,
  fieldsLength,
  metaAdsIntegrationId,
}) {
  const actionType = Form.useWatch(['actions', field.name, 'type'], form)
  const {
    token: { colorPrimary },
  } = theme.useToken()

  const activeStyle = {
    style: { borderColor: colorPrimary },
    styles: { header: { borderColor: colorPrimary } },
  }

  return (
    <div key={field.key} style={{ padding: 16, marginBottom: 16 }}>
      <Space style={{ width: '100%', marginBottom: 16 }} direction="vertical">
        <Row gutter={[8, 8]}>
          {loading &&
            new Array(4).fill().map((_, key) => (
              <Col span={6} key={key}>
                <Card
                  loading={loading}
                  title={<Skeleton active paragraph={false} />}
                >
                  <Skeleton active title={false} size="small">
                    <Card.Meta />
                  </Skeleton>
                </Card>
              </Col>
            ))}
          {!loading &&
            cards.map(({ title, description, icon, key }) => (
              <Col span={6} key={key}>
                <Card
                  size="small"
                  {...(actionType === key ? activeStyle : {})}
                  onClick={() =>
                    form.setFieldValue(['actions', field.name, 'type'], key)
                  }
                  title={
                    <>
                      {icon} {title}
                    </>
                  }
                  extra={
                    actionType === key && (
                      <CheckCircleFilled style={{ color: colorPrimary }} />
                    )
                  }
                  hoverable
                >
                  <Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }}>
                    {description}
                  </Typography.Paragraph>
                </Card>
              </Col>
            ))}
        </Row>
      </Space>

      <Form.Item
        name={[field.name, 'type']}
        initialValue={initValues?.type || 'WEBHOOK'}
        noStyle
      />
      <Form.Item name={[field.name, 'generativeTriggerID']} noStyle />
      <Form.Item name={[field.name, 'assistantID']} noStyle />
      <Form.Item name={[field.name, 'id']} noStyle />
      {actionType === 'WEBHOOK' && (
        <WebhookForm
          fieldName={field.name}
          form={form}
          initValues={initValues}
          parameters={trigger ? trigger?.args?.parameters : parameters}
        />
      )}
      {actionType === 'KNOWLEDGE' && (
        <KnowledgeForm
          form={form}
          fieldName={field.name}
          parameters={trigger ? trigger?.args?.parameters : parameters}
          assistantID={trigger ? trigger.assistantID : undefined}
        />
      )}

      {actionType === 'GOOGLE_CALENDAR' && (
        <GoogleCalendarForm
          fieldName={field.name}
          form={form}
          initValues={initValues}
          parameters={trigger ? trigger?.args?.parameters : parameters}
        />
      )}
      {actionType === 'NATIVE' && (
        <NativeForm
          fieldName={field.name}
          form={form}
          parameters={trigger ? trigger?.args?.parameters : parameters}
        />
      )}
      {actionType === 'CRM' && (
        <CRMForm
          fieldName={field.name}
          form={form}
          parameters={trigger ? trigger?.args?.parameters : parameters}
        />
      )}

      {actionType === 'CHAT_INTEGRATION' && (
        <ChatIntegrationForm fieldName={field.name} form={form} parameters={trigger ? trigger?.args?.parameters : parameters} />
      )}

      {actionType === 'CATALOG_INTEGRATION' && (
        <CatalogIntegrationForm
          fieldName={field.name}
          form={form}
          parameters={trigger ? trigger?.args?.parameters : parameters}
        />
      )}

      {actionType === 'META_ADS' && (
        <MetaAdsForm
          fieldName={field.name}
          form={form}
          integrationId={metaAdsIntegrationId}
        />
      )}
      {actionType === 'FEEGOW' && (
        <FeegowForm
          fieldName={field.name}
          form={form}
          parameters={trigger ? trigger?.args?.parameters : parameters}
        />
      )}

      {isTemplate && fieldsLength > 1 && (
        <Button
          type="dashed"
          danger
          onClick={() => remove(field.name)}
          style={{ marginTop: 8 }}
        >
          Remover Ação
        </Button>
      )}
    </div>
  )
}
